/**
 * Hand-off from the system browser back to the Task Mantis desktop app.
 *
 * After the user approves a Google / connector OAuth screen, the browser lands
 * on `<web origin>/desktop-callback`. That page forwards the one-time code to
 * the installed app through the `taskmantis://` custom link (registered by
 * public/electron.js), so the user never sees a loopback address.
 */

/** Custom URL scheme the desktop app registers. Keep in sync with public/desktopDeepLink.js. */
export const DESKTOP_PROTOCOL = "taskmantis";

/** Web route the browser ends on. */
export const DESKTOP_CALLBACK_PATH = "/desktop-callback";

/**
 * State suffix current desktop apps put on every OAuth state they start.
 * These returns finish on /desktop-callback and open the app via taskmantis://.
 */
export const DESKTOP_STATE_SUFFIX = "_desktop2";

/**
 * State suffix apps <= 0.1.13 used. Those apps only listen on the 127.0.0.1
 * loopback and cannot handle taskmantis://, so the web bounce keeps sending
 * them there. Do not emit this suffix from new code.
 */
export const LEGACY_DESKTOP_STATE_SUFFIX = "_desktop";

/** Loopback URL legacy (<= 0.1.13) desktop apps listen on. */
export const LEGACY_DESKTOP_LOOPBACK_URL = "http://127.0.0.1:51739/oauth-callback";

const statePrefix = (state) => String(state || "").split(".")[0];

/**
 * True for the Google sign-in state of either app generation.
 *
 * @example
 * isDesktopLoginState("login_desktop2"); // true
 * isDesktopLoginState("login_desktop"); // true (legacy app)
 * isDesktopLoginState("login"); // false
 */
export const isDesktopLoginState = (state) => {
  const value = String(state || "").trim().toLowerCase();
  return value === `login${DESKTOP_STATE_SUFFIX}` || value === `login${LEGACY_DESKTOP_STATE_SUFFIX}`;
};

/**
 * True when `state` came from a legacy (<= 0.1.13) desktop app.
 *
 * @example
 * isLegacyDesktopState("business_gong_popup_desktop.abc"); // true
 * isLegacyDesktopState("business_gong_popup_desktop2.abc"); // false
 */
export const isLegacyDesktopState = (state) =>
  statePrefix(state).endsWith(LEGACY_DESKTOP_STATE_SUFFIX);

/**
 * Pull code / state / error out of a query string.
 *
 * @example
 * readDesktopCallbackParams("?code=abc&state=login_desktop2");
 * // { code: "abc", state: "login_desktop2", error: "" }
 */
export const readDesktopCallbackParams = (search = "") => {
  const params = new URLSearchParams(search);
  return {
    code: String(params.get("code") || "").trim(),
    state: String(params.get("state") || "").trim(),
    error: String(params.get("error") || "").trim(),
  };
};

/**
 * True when `state` was minted by a current desktop app (`..._desktop2`,
 * optionally followed by a `.uuid`). Legacy `_desktop` states are not matched.
 *
 * @example
 * isDesktopState("business_gong_popup_desktop2.abc"); // true
 * isDesktopState("business_gong_popup_desktop.abc"); // false
 * isDesktopState("login"); // false
 */
export const isDesktopState = (state) =>
  statePrefix(state).endsWith(DESKTOP_STATE_SUFFIX);

/**
 * Build the `taskmantis://oauth-callback?...` link for a callback payload.
 * Returns "" when there is nothing to deliver.
 *
 * @example
 * buildDesktopDeepLink({ code: "abc", state: "login_desktop2", error: "" });
 * // "taskmantis://oauth-callback?code=abc&state=login_desktop2"
 */
export const buildDesktopDeepLink = ({ code, state, error } = {}) => {
  if (!code && !error) {
    return "";
  }
  const params = new URLSearchParams();
  if (code) params.set("code", code);
  if (state) params.set("state", state);
  if (error) params.set("error", error);
  return `${DESKTOP_PROTOCOL}://oauth-callback?${params.toString()}`;
};

/**
 * Build the in-browser URL the OAuth landing page should move to so the final
 * address is the trusted web origin. Carries the payload for exactly one page
 * load; the page strips it from the address bar immediately.
 *
 * @example
 * buildDesktopCallbackPageUrl("https://app.taskmantis.com", { code: "abc", state: "login_desktop2" });
 * // "https://app.taskmantis.com/desktop-callback?code=abc&state=login_desktop2"
 */
export const buildDesktopCallbackPageUrl = (origin, payload) => {
  const url = new URL(DESKTOP_CALLBACK_PATH, origin);
  if (payload?.code) url.searchParams.set("code", payload.code);
  if (payload?.state) url.searchParams.set("state", payload.state);
  if (payload?.error) url.searchParams.set("error", payload.error);
  return url.toString();
};

/**
 * Replace the address bar with the clean callback path (no code / state).
 *
 * @example
 * cleanDesktopCallbackAddressBar();
 * // address bar: https://app.taskmantis.com/desktop-callback
 */
export const cleanDesktopCallbackAddressBar = () => {
  try {
    window.history.replaceState(window.history.state, "", DESKTOP_CALLBACK_PATH);
  } catch (_) {
    // Cosmetic only.
  }
};

/**
 * Describe what the callback page should say for a payload.
 *
 * @example
 * describeDesktopCallback({ code: "", state: "x_desktop2", error: "access_denied" }).kind;
 * // "denied"
 */
export const describeDesktopCallback = (payload) => {
  const { code, state, error } = payload || {};
  if (!isDesktopState(state) || (!code && !error)) {
    return { kind: "missing", deepLink: "" };
  }
  if (error && !code) {
    return { kind: "denied", deepLink: buildDesktopDeepLink(payload) };
  }
  return { kind: "opening", deepLink: buildDesktopDeepLink(payload) };
};
